import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Typography, Grid, Tabs, Tab, Avatar, Chip, Divider, Alert, Snackbar, IconButton } from '@mui/material';
import { User, Heart, Package, MapPin, Home, ShoppingCart, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function UserDashboard() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [notificationOpen, setNotificationOpen] = useState(true);

  const orders = [
    { id: 'ORD001', date: '2026-03-15', status: 'Delivered', total: 299, items: ['Premium Wireless Headphones'] },
    { id: 'ORD002', date: '2026-03-10', status: 'Shipped', total: 500, items: ['Smart Watch', 'Phone Case'] },
  ];

  const wishlist = [
    { id: 1, title: 'Bluetooth Speaker', price: 89, originalPrice: 120, image: 'https://images.unsplash.com/photo-1608043152269-423dbba4e7e1?w=100&h=100&fit=crop', priceDrop: true, addedDate: '2026-03-20' },
    { id: 2, title: 'Laptop Stand', price: 45, originalPrice: 45, image: 'https://images.unsplash.com/photo-1527864550417-7fd91fc51a46?w=100&h=100&fit=crop', priceDrop: false, addedDate: '2026-03-18' },
    { id: 3, title: 'Wireless Headphones', price: 100, originalPrice: 150, image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=100&h=100&fit=crop', priceDrop: true, addedDate: '2026-03-15' },
  ];

  const addresses = [
    { id: 1, type: 'Home', address: '123 Main St, City, State 12345', default: true },
    { id: 2, type: 'Work', address: '456 Office Blvd, City, State 67890', default: false },
  ];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const renderTabContent = () => {
    switch (tabValue) {
      case 0: // Orders
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>Your Orders</Typography>
            {orders.map((order) => (
              <Card key={order.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">Order #{order.id}</Typography>
                    <Chip label={order.status} color={order.status === 'Delivered' ? 'success' : 'primary'} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">{order.date}</Typography>
                  <Typography variant="body2">{order.items.join(', ')}</Typography>
                  <Typography variant="h6" sx={{ mt: 1 }}>${order.total}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        );
      case 1: // Wishlist
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Your Wishlist ({wishlist.length} items)</Typography>
              <Button variant="outlined" size="small">Clear All</Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              💰 Get notified when prices drop! We'll send you alerts for items in your wishlist.
            </Typography>
            <Grid container spacing={2}>
              {wishlist.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <Card sx={{ position: 'relative' }}>
                    {item.priceDrop && (
                      <Chip
                        label="Price Dropped!"
                        color="error"
                        size="small"
                        sx={{ position: 'absolute', top: 10, left: 10, zIndex: 1 }}
                      />
                    )}
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <img
                          src={item.image}
                          alt={item.title}
                          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                            {item.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Typography variant="h6" color="primary">
                              ${item.price}
                            </Typography>
                            {item.originalPrice > item.price && (
                              <Typography
                                variant="body2"
                                sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                              >
                                ${item.originalPrice}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Added {item.addedDate}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="contained" size="small" fullWidth>
                          Add to Cart
                        </Button>
                        <Button variant="outlined" size="small" color="error">
                          Remove
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {wishlist.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Heart size={64} color="#ccc" />
                <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
                  Your wishlist is empty
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start adding items you love!
                </Typography>
                <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/ShopPage')}>
                  Browse Products
                </Button>
              </Box>
            )}
          </Box>
        );
      case 2: // Profile
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>Profile Information</Typography>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar sx={{ width: 80, height: 80 }}>
                    <User size={40} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5">John Doe</Typography>
                    <Typography variant="body2" color="text.secondary">john.doe@example.com</Typography>
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Phone</Typography>
                    <Typography>+1 (555) 123-4567</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Member Since</Typography>
                    <Typography>January 2026</Typography>
                  </Grid>
                </Grid>
                <Button variant="outlined" sx={{ mt: 3 }}>Edit Profile</Button>
              </CardContent>
            </Card>
          </Box>
        );
      case 3: // Addresses
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Your Addresses</Typography>
              <Button variant="contained">Add New Address</Button>
            </Box>
            {addresses.map((addr) => (
              <Card key={addr.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1">{addr.type}</Typography>
                      <Typography variant="body2">{addr.address}</Typography>
                    </Box>
                    {addr.default && <Chip label="Default" color="primary" size="small" />}
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Button size="small">Edit</Button>
                    <Button size="small" color="error" sx={{ ml: 1 }}>Delete</Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ mt: 7, mx: 2, maxWidth: '1000px', mx: 'auto' }}>
      {/* Price Drop Notification */}
      <Snackbar 
        open={notificationOpen} 
        onClose={() => setNotificationOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setNotificationOpen(false)} 
          severity="success" 
          sx={{ width: '100%' }}
          icon={<Bell />}
        >
          🎉 Great news! 2 items in your wishlist have price drops! Check them out now.
        </Alert>
      </Snackbar>

      {/* Simple Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, p: 2, bgcolor: 'white', borderRadius: 1, boxShadow: 1 }}>
        <Button startIcon={<Home />} onClick={() => navigate('/Ecommerce')}>Home</Button>
        <Typography variant="h5">My Account</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={() => navigate('/CartPage')}><ShoppingCart /></IconButton>
          <IconButton onClick={() => navigate('/UserDashboard')}><User /></IconButton>
        </Box>
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 4 }}>
        <Tab icon={<Package />} label="Orders" />
        <Tab icon={<Heart />} label="Wishlist" />
        <Tab icon={<User />} label="Profile" />
        <Tab icon={<MapPin />} label="Addresses" />
      </Tabs>

      {renderTabContent()}
    </Box>
  );
}

export default UserDashboard;